import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { API } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Badge } from "../components/Badge";
import { fmtDateTime } from "../lib/format";
import { MessageSquare, Send, Search, User, Bot, Headphones, Loader2, Image as ImageIcon, RefreshCw, Inbox, ShieldOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

function mediaUrl(path) {
  // Messages sent by users reference /api/site/... (user endpoint, 403 for admin).
  // Rewrite those to the admin-side endpoint /api/live-chat/image/{id} so admin can fetch.
  if (!path) return "";
  const m = path.match(/\/profile\/support\/chat\/image\/([a-f0-9]+)/i);
  if (m) {
    return `${API}/live-chat/image/${m[1]}`;
  }
  if (/^https?:/i.test(path)) return path;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}${path}`;
}

function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const today = new Date();
    const same = d.toDateString() === today.toDateString();
    if (same) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

export default function LiveChat() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSession, setActiveSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [userInfo, setUserInfo] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const threadRef = useRef(null);
  const pollRef = useRef(null);

  const loadSessions = async () => {
    try {
      const { data } = await api.get("/live-chat/sessions", {
        params: {
          search: search || undefined,
          unread_only: unreadOnly || undefined,
        },
      });
      setSessions(data?.items || []);
    } catch (e) {
      toast.error("Session লোড করতে সমস্যা হয়েছে");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSessions();
    const t = setInterval(loadSessions, 6000); // refresh sidebar every 6s
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, unreadOnly]);

  const openSession = async (s) => {
    setActiveSession(s);
    setMessages([]);
    setUserInfo(null);
    try {
      const { data } = await api.get(`/live-chat/sessions/${s.session_id}/messages`);
      setMessages(data?.items || []);
      setUserInfo(data?.user || null);
      // Mark session read locally
      setSessions((prev) => prev.map((x) => x.session_id === s.session_id ? { ...x, unread_for_admin: 0 } : x));
      setTimeout(() => {
        if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
      }, 60);
    } catch {
      toast.error("Chat thread load ব্যর্থ হয়েছে");
    }
  };

  // Poll the active thread for new messages every 3.5s
  useEffect(() => {
    if (!activeSession) return;
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/live-chat/sessions/${activeSession.session_id}/messages`);
        const items = data?.items || [];
        setMessages((prev) => {
          if (items.length !== prev.length) {
            setTimeout(() => {
              if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
            }, 50);
            return items;
          }
          return prev;
        });
      } catch { /* ignore */ }
    }, 3500);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [activeSession]);

  const sendReply = async () => {
    if (!activeSession || !reply.trim() || sending) return;
    setSending(true);
    const text = reply.trim();
    setReply("");
    try {
      const { data } = await api.post(`/live-chat/sessions/${activeSession.session_id}/reply`, { text });
      if (data?.message) {
        setMessages((m) => [...m, data.message]);
        setTimeout(() => {
          if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
        }, 50);
      }
      loadSessions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reply পাঠাতে সমস্যা হয়েছে");
      setReply(text);
    } finally {
      setSending(false);
    }
  };

  const toggleBlock = async () => {
    if (!activeSession || !userInfo) return;
    const willBlock = !userInfo.chat_blocked;
    const reason = willBlock
      ? (window.prompt("Block reason (user-কে দেখানো হবে):", "Live chat access has been disabled by admin.") || "").trim()
      : "";
    if (willBlock && !reason) return; // user cancelled
    if (!willBlock && !window.confirm(`${userInfo.username}-কে chat এ unblock করবেন?`)) return;
    try {
      const endpoint = willBlock ? "block" : "unblock";
      const payload = willBlock ? { reason } : {};
      const { data } = await api.post(`/live-chat/sessions/${activeSession.session_id}/${endpoint}`, payload);
      setUserInfo((u) => ({ ...u, chat_blocked: !!data?.chat_blocked, chat_block_reason: data?.chat_block_reason || "" }));
      toast.success(willBlock ? `${userInfo.username}-কে chat এ block করা হয়েছে` : `${userInfo.username}-কে unblock করা হয়েছে`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Block toggle ব্যর্থ হয়েছে");
    }
  };

  return (
    <div data-testid="livechat-page">
      <PageHeader
        title="Live Chat"
        subtitle="ইউজারদের সাথে real-time কথা বলুন — AI চ্যাটে admin direct reply পাঠাতে পারেন"
      />

      {/* Filters */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Username, name বা phone খুঁজুন..."
            className="w-full pl-9 pr-3 h-10 border border-gray-200 rounded-sm text-sm focus:outline-none focus:border-emerald-500"
            data-testid="livechat-search"
          />
        </div>
        <button
          onClick={() => setUnreadOnly((v) => !v)}
          className={`h-10 px-4 rounded-sm text-sm border transition-colors inline-flex items-center gap-2 ${
            unreadOnly ? "bg-emerald-500 text-white border-emerald-500" : "bg-white text-gray-700 border-gray-200 hover:border-gray-400"
          }`}
          data-testid="livechat-filter-unread"
        >
          <Inbox size={14} /> শুধু unread
        </button>
        <button
          onClick={loadSessions}
          className="h-10 px-3 rounded-sm text-sm border bg-white text-gray-700 border-gray-200 hover:border-gray-400 inline-flex items-center gap-2"
          data-testid="livechat-refresh"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-4 min-h-[640px]">
        {/* Session list */}
        <div className="bg-white border border-gray-200 rounded-sm overflow-hidden flex flex-col" data-testid="livechat-sessions">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
            <div className="text-[11px] uppercase tracking-widest text-gray-600 font-bold flex items-center gap-1.5">
              <MessageSquare size={13} /> Chat Sessions
            </div>
            <span className="text-[10px] text-gray-500 font-mono">{sessions.length}</span>
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-gray-100 max-h-[640px]">
            {loading ? (
              <div className="p-8 text-center text-sm text-gray-500 flex items-center justify-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Loading...
              </div>
            ) : sessions.length === 0 ? (
              <div className="p-8 text-center text-sm text-gray-500 flex flex-col items-center gap-2">
                <Inbox size={26} className="text-gray-300" />
                <span>এখনো কোনো chat session নেই।</span>
              </div>
            ) : (
              sessions.map((s) => {
                const unread = (s.unread_for_admin || 0) > 0;
                const active = activeSession?.session_id === s.session_id;
                return (
                  <button
                    key={s.session_id}
                    onClick={() => openSession(s)}
                    className={`w-full text-left px-4 py-3 transition-colors relative ${
                      active ? "bg-emerald-50/50 border-l-2 border-l-emerald-500" : "hover:bg-gray-50 border-l-2 border-l-transparent"
                    }`}
                    data-testid={`livechat-session-${s.username || s.user_id}`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <div
                          className="h-8 w-8 rounded-full flex items-center justify-center text-white font-bold text-xs shrink-0"
                          style={{ background: "linear-gradient(135deg, #19a55c 0%, #0f5e34 100%)" }}
                        >
                          {(s.username || "U").charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className={`text-sm truncate ${unread ? "font-bold text-gray-900" : "font-semibold text-gray-800"}`}>
                            {s.username || "Unknown"}
                          </div>
                          {s.full_name && (
                            <div className="text-[10px] text-gray-500 truncate">{s.full_name}</div>
                          )}
                        </div>
                      </div>
                      <div className="text-[10px] text-gray-500 font-mono shrink-0">{fmtTime(s.last_message_at)}</div>
                    </div>
                    <div className={`text-xs line-clamp-2 pr-1 ${unread ? "text-gray-800" : "text-gray-500"}`}>
                      {s.last_message || "—"}
                    </div>
                    {unread && (
                      <span
                        className="absolute top-3 right-3 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1"
                        data-testid="livechat-unread-badge"
                      >
                        {s.unread_for_admin}
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Thread */}
        <div className="bg-white border border-gray-200 rounded-sm flex flex-col min-h-[640px]" data-testid="livechat-thread">
          {!activeSession ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-12 text-gray-500">
              <MessageSquare size={42} className="text-gray-300 mb-3" />
              <div className="font-semibold text-gray-700">একটি session বেছে নিন</div>
              <div className="text-xs text-gray-500 mt-1">বাম পাশ থেকে যেকোনো user-এর chat খুলে reply পাঠান</div>
            </div>
          ) : (
            <>
              {/* Thread header */}
              <div className="px-5 py-3 border-b border-gray-200 flex items-center gap-3 bg-gradient-to-r from-emerald-50/40 to-transparent">
                <div
                  className="h-10 w-10 rounded-full flex items-center justify-center text-white font-bold"
                  style={{ background: "linear-gradient(135deg, #19a55c 0%, #0f5e34 100%)" }}
                >
                  {(activeSession.username || "U").charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-gray-900 truncate flex items-center gap-2">
                    {activeSession.username}
                    {userInfo?.full_name && <span className="text-gray-500 font-normal text-sm">({userInfo.full_name})</span>}
                    {userInfo?.chat_blocked && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-red-50 text-red-600 border border-red-200"
                        title={userInfo?.chat_block_reason || ""}
                      >
                        <ShieldOff size={10} /> Chat Blocked
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-[11px] text-gray-500 mt-0.5 flex-wrap">
                    {userInfo?.email && <span>{userInfo.email}</span>}
                    {userInfo?.phone && <span>· {userInfo.phone}</span>}
                    {userInfo && <span>· Balance ৳{userInfo.balance?.toFixed(2)}</span>}
                    {userInfo?.vip_tier && <Badge variant={userInfo.vip_tier === "Normal" ? "default" : "success"}>{userInfo.vip_tier}</Badge>}
                    {userInfo?.kyc_status && userInfo.kyc_status !== "unverified" && (
                      <Badge variant={userInfo.kyc_status === "verified" ? "success" : "pending"}>KYC: {userInfo.kyc_status}</Badge>
                    )}
                  </div>
                </div>
                {/* Block / Unblock toggle */}
                {userInfo && (
                  <button
                    type="button"
                    onClick={toggleBlock}
                    className={`h-9 px-3 rounded-sm text-xs font-bold inline-flex items-center gap-1.5 border transition-colors ${
                      userInfo.chat_blocked
                        ? "bg-emerald-500 hover:bg-emerald-600 text-white border-emerald-500"
                        : "bg-white hover:bg-red-50 text-red-600 border-red-300 hover:border-red-400"
                    }`}
                    title={userInfo.chat_blocked ? "Unblock user" : "Block user from chat"}
                    data-testid="livechat-block-btn"
                  >
                    {userInfo.chat_blocked ? (
                      <><ShieldCheck size={13} /> Unblock</>
                    ) : (
                      <><ShieldOff size={13} /> Block</>
                    )}
                  </button>
                )}
              </div>

              {/* Messages */}
              <div ref={threadRef} className="flex-1 overflow-y-auto p-5 space-y-3 bg-gray-50" data-testid="livechat-messages">
                {messages.length === 0 ? (
                  <div className="text-center text-sm text-gray-500 py-8">কোনো মেসেজ নেই।</div>
                ) : (
                  messages.map((m) => <AdminBubble key={m.id} m={m} />)
                )}
              </div>

              {/* Composer */}
              {userInfo?.chat_blocked && (
                <div className="px-4 py-2 bg-red-50 border-t border-red-200 text-[11px] text-red-700 inline-flex items-center gap-2">
                  <ShieldOff size={13} />
                  <span>This user is currently <b>blocked</b> from sending messages. Reason: {userInfo.chat_block_reason || "—"}</span>
                </div>
              )}
              <form
                onSubmit={(e) => { e.preventDefault(); sendReply(); }}
                className="border-t border-gray-200 p-3 flex items-end gap-2 bg-white"
                data-testid="livechat-composer"
              >
                <div className="flex-1 rounded-sm border border-gray-200 focus-within:border-emerald-500 transition-colors">
                  <textarea
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        sendReply();
                      }
                    }}
                    rows={2}
                    placeholder="User-কে বাংলায় reply করুন... (Enter = send)"
                    className="w-full px-3 py-2 text-sm outline-none resize-none"
                    data-testid="livechat-reply-input"
                    disabled={sending}
                  />
                </div>
                <button
                  type="submit"
                  disabled={!reply.trim() || sending}
                  className="h-11 px-5 rounded-sm bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="livechat-send-btn"
                >
                  {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  পাঠান
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function AdminBubble({ m }) {
  const isUser = m.role === "user";
  const isAdmin = m.role === "admin";
  const isAssistant = m.role === "assistant";

  // User on LEFT, bot/admin on RIGHT (admin-view mirror of site)
  const side = isUser ? "justify-start" : "justify-end";

  const headerLabel = isUser ? (m.username || "Player") : (isAdmin ? (m.admin_name || "Admin (You)") : "Maxi AI");
  const Icon = isUser ? User : (isAdmin ? Headphones : Bot);

  const bubbleStyle = isUser
    ? { background: "white", borderColor: "#e5e7eb", color: "#1f2937" }
    : isAdmin
    ? { background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", borderColor: "transparent", color: "white" }
    : { background: "linear-gradient(135deg, #f3e8ff 0%, #ede9fe 100%)", borderColor: "#c4b5fd", color: "#4c1d95" };

  return (
    <div className={`flex ${side}`} data-testid={`livechat-msg-${m.role}`}>
      <div className="max-w-[75%]">
        <div className={`flex items-center gap-1.5 mb-1 ${isUser ? "" : "justify-end"}`}>
          <Icon size={11} className={isUser ? "text-gray-500" : (isAdmin ? "text-emerald-600" : "text-violet-600")} />
          <span className={`text-[10px] font-bold uppercase tracking-wider ${isUser ? "text-gray-600" : (isAdmin ? "text-emerald-700" : "text-violet-700")}`}>
            {headerLabel}
          </span>
          <span className="text-[10px] text-gray-400">{fmtTime(m.created_at)}</span>
        </div>
        {m.image_url && (
          <a href={mediaUrl(m.image_url)} target="_blank" rel="noreferrer" className="block mb-2">
            <img
              src={mediaUrl(m.image_url)}
              alt="attachment"
              className="rounded-sm max-h-56 object-cover border border-gray-300"
              data-testid="livechat-msg-image"
            />
          </a>
        )}
        {m.text && (
          <div
            className="rounded-sm px-3 py-2 text-sm leading-relaxed border whitespace-pre-wrap break-words"
            style={bubbleStyle}
          >
            {m.text}
          </div>
        )}
        {m.handoff && (
          <div className={`mt-1 ${isUser ? "" : "text-right"}`}>
            <span className="inline-flex items-center gap-1 px-1.5 h-4 rounded-sm bg-amber-100 text-amber-700 text-[9px] font-bold uppercase tracking-wider">
              <Headphones size={9} /> Handoff
            </span>
          </div>
        )}
        {isAssistant && (
          <div className={`mt-1 text-right`}>
            <span className="inline-flex items-center gap-1 px-1.5 h-4 rounded-sm bg-violet-100 text-violet-700 text-[9px] font-bold uppercase tracking-wider">
              <Bot size={9} /> AI reply
            </span>
          </div>
        )}
        {m.image_url && !m.text && (
          <div className="text-[10px] text-gray-400 mt-1 inline-flex items-center gap-1">
            <ImageIcon size={9} /> image-only
          </div>
        )}
      </div>
    </div>
  );
}
